import React, { useState } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import { ChevronLeft, ChevronRight, X, Calendar, Home, User, Download, Upload } from 'lucide-react';
import { Photo, Reservation, House } from '../../services/types';

interface ReservationPhotoCarouselProps {
    show: boolean;
    onHide: () => void;
    reservation: Reservation | null;
    house: House | null;
    checkInPhotos: Photo[];
    checkOutPhotos: Photo[];
    onDelete?: (publicId: string) => void;
    onUploadClick?: () => void;
    showUploadButton?: boolean;
}

const ReservationPhotoCarousel: React.FC<ReservationPhotoCarouselProps> = ({
    show,
    onHide,
    reservation,
    house,
    checkInPhotos,
    checkOutPhotos,
    onDelete,
    onUploadClick,
    showUploadButton = false,
}) => {
    const [activeType, setActiveType] = useState<'check-in' | 'check-out'>('check-in');
    const [currentIndex, setCurrentIndex] = useState(0);

    const currentPhotos = activeType === 'check-in' ? checkInPhotos : checkOutPhotos;
    const currentPhoto = currentPhotos[currentIndex];

    const handleNext = () => {
        if (currentIndex < currentPhotos.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleTypeChange = (type: 'check-in' | 'check-out') => {
        setActiveType(type);
        setCurrentIndex(0); // Reset to first photo when switching type
    };

    const handleClose = () => {
        setCurrentIndex(0);
        setActiveType('check-in');
        onHide();
    };

    const handleDelete = async () => {
        if (currentPhoto && onDelete) {
            await onDelete(currentPhoto.public_id);
            // If this was the last photo in the current type, switch to the other type
            if (currentPhotos.length === 1) {
                const otherType = activeType === 'check-in' ? 'check-out' : 'check-in';
                const otherPhotos = otherType === 'check-in' ? checkInPhotos : checkOutPhotos;
                if (otherPhotos.length > 0) {
                    setActiveType(otherType);
                } else {
                    handleClose();
                }
            } else if (currentIndex >= currentPhotos.length - 1) {
                setCurrentIndex(Math.max(0, currentPhotos.length - 2));
            }
        }
    };

    const handleDownload = async () => {
        if (currentPhoto) {
            try {
                // Fetch the image as a blob to bypass CORS issues
                const response = await fetch(currentPhoto.url);
                const blob = await response.blob();

                // Create a blob URL
                const blobUrl = window.URL.createObjectURL(blob);

                // Create download link
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `${reservation?.name_first_guess || 'guest'}_${activeType}_${currentIndex + 1}.jpg`;
                document.body.appendChild(link);
                link.click();

                // Cleanup
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (error) {
                console.error('Download failed:', error);
                // Fallback to opening in new tab if fetch fails
                window.open(currentPhoto.url, '_blank');
            }
        }
    };

    if (!reservation) return null;

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered className="photo-carousel-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="w-100">
                    <Modal.Title className="mb-2">
                        <User size={20} className="me-2" />
                        {reservation.name_first_guess || 'Guest'}
                    </Modal.Title>
                    <div className="d-flex gap-3 text-muted small">
                        <span>
                            <Home size={16} className="me-1" />
                            {house?.name || house?.address || 'Unknown'}
                        </span>
                        <span>
                            <Calendar size={16} className="me-1" />
                            {reservation.check_in_date} - {reservation.check_out_date}
                        </span>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="p-0">
                {/* Type Selector */}
                <div className="d-flex justify-content-center gap-2 p-3 border-bottom">
                    <Button
                        variant={activeType === 'check-in' ? 'primary' : 'outline-primary'}
                        onClick={() => handleTypeChange('check-in')}
                        disabled={checkInPhotos.length === 0}
                    >
                        Check-in Photos
                        {checkInPhotos.length > 0 && (
                            <Badge bg="light" text="dark" className="ms-2">
                                {checkInPhotos.length}
                            </Badge>
                        )}
                    </Button>
                    <Button
                        variant={activeType === 'check-out' ? 'primary' : 'outline-primary'}
                        onClick={() => handleTypeChange('check-out')}
                        disabled={checkOutPhotos.length === 0}
                    >
                        Check-out Photos
                        {checkOutPhotos.length > 0 && (
                            <Badge bg="light" text="dark" className="ms-2">
                                {checkOutPhotos.length}
                            </Badge>
                        )}
                    </Button>
                </div>

                {/* Photo Display */}
                {currentPhotos.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <p>No {activeType} photos available</p>
                    </div>
                ) : (
                    <div className="position-relative" style={{ backgroundColor: '#000', minHeight: '400px' }}>
                        {/* Main Photo */}
                        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                            <img
                                src={currentPhoto.url}
                                alt={`${activeType} photo ${currentIndex + 1}`}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain',
                                }}
                            />
                        </div>

                        {/* Navigation Arrows */}
                        {currentPhotos.length > 1 && (
                            <>
                                <Button
                                    variant="dark"
                                    className="position-absolute top-50 start-0 translate-middle-y ms-3"
                                    onClick={handlePrev}
                                    disabled={currentIndex === 0}
                                    style={{
                                        borderRadius: '50%',
                                        width: '50px',
                                        height: '50px',
                                        opacity: 0.8,
                                    }}
                                >
                                    <ChevronLeft size={24} />
                                </Button>
                                <Button
                                    variant="dark"
                                    className="position-absolute top-50 end-0 translate-middle-y me-3"
                                    onClick={handleNext}
                                    disabled={currentIndex === currentPhotos.length - 1}
                                    style={{
                                        borderRadius: '50%',
                                        width: '50px',
                                        height: '50px',
                                        opacity: 0.8,
                                    }}
                                >
                                    <ChevronRight size={24} />
                                </Button>
                            </>
                        )}

                        {/* Photo Counter */}
                        <div
                            className="position-absolute bottom-0 start-50 translate-middle-x mb-3 px-3 py-1 bg-dark text-white rounded"
                            style={{ opacity: 0.8 }}
                        >
                            {currentIndex + 1} / {currentPhotos.length}
                        </div>
                    </div>
                )}

                {/* Thumbnail Strip */}
                {currentPhotos.length > 1 && (
                    <div className="p-3 border-top bg-light">
                        <div className="d-flex gap-2 overflow-auto">
                            {currentPhotos.map((photo, index) => (
                                <img
                                    key={photo.public_id}
                                    src={photo.url}
                                    alt={`Thumbnail ${index + 1}`}
                                    onClick={() => setCurrentIndex(index)}
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                        border: currentIndex === index ? '3px solid #0d6efd' : '2px solid #dee2e6',
                                        borderRadius: '4px',
                                        opacity: currentIndex === index ? 1 : 0.6,
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0">
                {currentPhoto && (
                    <Button variant="success" onClick={handleDownload}>
                        <Download size={18} className="me-2" />
                        Download
                    </Button>
                )}
                {showUploadButton && onUploadClick && (
                    <Button variant="primary" onClick={onUploadClick}>
                        <Upload size={18} className="me-2" />
                        Upload Photos
                    </Button>
                )}
                {onDelete && currentPhoto && (
                    <Button variant="danger" onClick={handleDelete}>
                        <X size={18} className="me-2" />
                        Delete Photo
                    </Button>
                )}
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReservationPhotoCarousel;
